import React from 'react';
import { Flex, List, Table, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { RedTimes, TableView, TitleTag } from '~/stylizations/styles';
import { formatPrice, parsePrice } from '~/Utils/format';
import Button from '~/components/Button';

export default function Client({ values, handlePdf, isMobile }) {
  const { t } = useTranslation();

  const label = (text) => <span style={{ fontSize: '18px', fontWeight: 'bold' }}>{t(text)}</span>;
  const mobileLabel = (text) => (
    <div>
      <span style={{ fontSize: '15px', fontWeight: 'bold' }}>{t(text)}</span>
    </div>
  );
  const child = (text) => {
    if (text == null || text == '') {
      return (
        <div style={{ textAlign: 'center' }}>
          <RedTimes />
        </div>
      );
    }

    return <span style={{ fontSize: isMobile ? '12px' : '15px', fontWeight: 'bold' }}>{t(`${text}`)}</span>;
  };

  const totalPayments = values.event_financial_payments?.reduce((acc, p) => {
    const price = parsePrice(p.price);
    return acc + price;
  }, 0);

  const paymentsFields = (values?.event_financial_payments?.length ? values.event_financial_payments : [{}]).flatMap(
    (payment, i) => [
      { label: `Pagamento ${i + 1} - Nome`, value: payment?.name },
      { label: `Pagamento ${i + 1} - Valor`, value: payment?.price },
    ]
  );

  const lossesFields = (values?.event_financial_losses?.length ? values.event_financial_losses : [{}]).flatMap(
    (loss, i) => [
      { label: `Pagamento ${i + 1} - Nome`, value: loss?.name },
      { label: `Pagamento ${i + 1} - Valor`, value: loss?.price },
    ]
  );

  const data = [...paymentsFields, ...lossesFields];

  return isMobile ? (
    <List
      bordered
      dataSource={data}
      renderItem={({ label, value }) => (
        <List.Item>
          <Typography.Text strong>{mobileLabel(label)}</Typography.Text>
          {child(value)}
        </List.Item>
      )}
    />
  ) : (
    <>
      <Flex align="center" justify="end">
        <Button color="primary" onClick={() => handlePdf(values, 'client-report')}>
          {t('Emitir fechamento cliente')}
        </Button>
      </Flex>
      <TitleTag>{label('TAXAS')}</TitleTag>
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Taxa de antecipação (%)'),
            dataIndex: 'anticipation_fee',
            render: child,
          },
          {
            title: label('Taxa débito do cliente (%)'),
            dataIndex: 'debit_fee',
            render: child,
          },
          {
            title: label('Taxa crédito do cliente (%)'),
            dataIndex: 'credit_fee',
            render: child,
          },
          {
            title: label('Taxa pix do cliente (%)'),
            dataIndex: 'pix_fee',
            render: child,
          },
        ]}
        dataSource={[values]}
      />
      <TitleTag>{label('VALORES')}</TitleTag>
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Débito bruto'),
            dataIndex: 'debit_raw_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Taxa débito - ' + values?.debit_fee + '%'),
            dataIndex: 'debit_fee_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Débito líquido'),
            dataIndex: 'debit_final_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
        ]}
        dataSource={[values]}
      />
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Crédito bruto'),
            dataIndex: 'credit_raw_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Taxa crédito - ' + values?.credit_fee + '%'),
            dataIndex: 'credit_fee_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Crédito líquido'),
            dataIndex: 'credit_final_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
        ]}
        dataSource={[values]}
      />
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Pix bruto'),
            dataIndex: 'pix_raw_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Taxa pix - ' + values?.pix_fee + '%'),
            dataIndex: 'pix_fee_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Pix líquido'),
            dataIndex: 'pix_final_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
        ]}
        dataSource={[values]}
      />
      <TitleTag>{label('TOTAL')}</TitleTag>
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Total bruto (débito + crédito + pix)'),
            dataIndex: 'total_raw_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
          {
            title: label('Total líquido (débito + crédito + pix'),
            dataIndex: 'total_liquid_client',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
        ]}
        dataSource={[values]}
      />
      <TitleTag>{label('PAGAMENTOS')}</TitleTag>
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            title: label('Descrição'),
            dataIndex: 'name',
            render: child,
          },
          {
            title: label('Valor'),
            dataIndex: 'price',
            render: child,
          },
        ]}
        dataSource={values.event_financial_payments}
        summary={() => {
          return (
            <Table.Summary.Row style={{ backgroundColor: '#d6d6d6' }}>
              <Table.Summary.Cell>{t('TOTAL')}</Table.Summary.Cell>
              <Table.Summary.Cell>{formatPrice(totalPayments)}</Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
      <TitleTag>{label('TOTAL FINAL')}</TitleTag>
      <TableView
        pagination={false}
        rowKey={() => 'basic-1'}
        bordered
        columns={[
          {
            dataIndex: 'customer_final_price',
            render: (text, _) => child(formatPrice(parsePrice(text))),
          },
        ]}
        dataSource={[values]}
      />
    </>
  );
}
